import { useState } from 'react';

import type {
  Abrangencia,
  Recorrencia,
  RegraForense,
  RegraForenseInput,
  SugestaoForense,
  TipoRegra,
} from '@/api/calendarioForense';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useNotification } from '@/context/NotificationProvider';
import { usePromoverSugestao, useSalvarRegraForense } from '@/hooks/useCalendarioForense';
import { classifyError, getUserVisibleErrorMessage, notifyMutationError } from '@/lib/errors';
import {
  ABRANGENCIAS,
  JUSTICAS,
  RECORRENCIAS,
  TIPOS,
  UFS,
  hojeIso,
  lerOffsetPascoa,
  regraParaInput,
  regraVazia,
} from './calendarioForense';
import {
  ExcecoesEditor,
  adaptarLinhas,
  anoRepetido,
  excecoesParaLinhas,
  linhasParaExcecoes,
  type LinhaExcecao,
} from './ExcecoesEditor';

interface Props {
  /** `null` = nova regra. Montado com `key`: o formulário começa da regra a cada abertura. */
  regra: RegraForense | null;
  /** Promover a sugestão de um advogado: o formulário parte dela e grava como oficial. */
  sugestao?: SugestaoForense;
  onClose: () => void;
}

function vazioParaNull(texto: string | null): string | null {
  const t = (texto ?? '').trim();
  return t ? t : null;
}

export function RegraForenseDialog({ regra, sugestao, onClose }: Props) {
  const { showSuccess, showError } = useNotification();
  const salvarRegra = useSalvarRegraForense();
  const promover = usePromoverSugestao();
  const salvar = sugestao ? promover : salvarRegra;
  const [form, setForm] = useState<RegraForenseInput>(() => {
    const base = sugestao ?? regra;
    return base ? regraParaInput(base) : regraVazia();
  });
  const [ibge, setIbge] = useState(form.municipio_ibge ? String(form.municipio_ibge) : '');
  const [offset, setOffset] = useState(form.offset_pascoa !== null ? String(form.offset_pascoa) : '');
  const [linhas, setLinhas] = useState<LinhaExcecao[]>(() => excecoesParaLinhas(form.excecoes));
  const [erro, setErro] = useState<string | null>(null);

  const set = <K extends keyof RegraForenseInput>(campo: K, valor: RegraForenseInput[K]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const { recorrencia, abrangencia } = form;
  const tipo = TIPOS.find((t) => t.value === form.tipo);

  const alternarJustica = (valor: (typeof JUSTICAS)[number]['value']) => {
    const atual = form.justicas ?? [];
    const novo = atual.includes(valor) ? atual.filter((j) => j !== valor) : [...atual, valor];
    set('justicas', novo.length ? novo : null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const offsetLido = lerOffsetPascoa(offset);
    const repetido = recorrencia === 'unica' ? null : anoRepetido(linhas);
    const faltando = !form.nome.trim()
      ? 'Informe o nome.'
      : recorrencia !== 'pascoa' && !form.data
        ? 'Informe a data.'
        : recorrencia === 'pascoa' && offsetLido === null
          ? 'Informe quantos dias antes (negativo) ou depois da Páscoa, de −100 a 100.'
          : !form.ato_normativo.trim()
            ? 'Informe o ato que declara o dia (lei, resolução, portaria).'
            : repetido
              ? `Há duas exceções para ${repetido}.`
              : null;
    setErro(faltando);
    if (faltando) return;
    const input: RegraForenseInput = {
      ...form,
      nome: form.nome.trim(),
      ato_normativo: form.ato_normativo.trim(),
      observacao: vazioParaNull(form.observacao),
      // A data não entra na conta da Páscoa, mas a coluna é obrigatória.
      data: recorrencia === 'pascoa' ? form.data || hojeIso() : form.data,
      data_fim: recorrencia === 'periodo_anual' || recorrencia === 'unica' ? form.data_fim || null : null,
      offset_pascoa: recorrencia === 'pascoa' ? offsetLido : null,
      excecoes: recorrencia === 'unica' ? null : linhasParaExcecoes(linhas),
      uf: vazioParaNull(form.uf),
      municipio_ibge: ibge.trim() ? Number(ibge) : null,
      municipio_nome: vazioParaNull(form.municipio_nome),
      tribunal: vazioParaNull(form.tribunal),
      orgao: vazioParaNull(form.orgao),
    };
    const callbacks = {
      onSuccess: () => {
        showSuccess(
          sugestao
            ? 'Promovida ao calendário oficial. Os vencimentos afetados serão refeitos.'
            : regra
              ? 'Regra salva. Os vencimentos afetados serão refeitos.'
              : 'Regra criada. Os vencimentos afetados serão refeitos.',
        );
        onClose();
      },
      onError: (err: unknown) => {
        // Regra incoerente (422) e sugestão já resolvida (409) são do formulário;
        // o resto vai ao helper central.
        const status = classifyError(err).status;
        if (status === 422 || status === 409) {
          setErro(getUserVisibleErrorMessage(err));
          return;
        }
        notifyMutationError(err, showError);
      },
    };
    if (sugestao) promover.mutate({ id: sugestao.id, input }, callbacks);
    else salvarRegra.mutate({ id: regra?.id, input }, callbacks);
  };

  return (
    // Fechar com o salvamento em curso perderia o erro (o callback não roda desmontado).
    <Dialog open onOpenChange={(open) => !open && !salvar.isPending && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {sugestao ? 'Promover ao calendário oficial' : regra ? 'Editar regra' : 'Nova regra'}
          </DialogTitle>
          <DialogDescription>
            {sugestao
              ? 'Confira o ato e ajuste o que for preciso: a regra passa a valer para todos, e a do advogado é desligada (a oficial a substitui).'
              : 'Vale para a conta de todos os usuários. Ao salvar, os vencimentos que a regra alcança são refeitos e a agenda acompanha.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="regra-nome">Nome</Label>
            <Input id="regra-nome" value={form.nome} maxLength={200} onChange={(e) => set('nome', e.target.value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="regra-tipo">Efeito</Label>
              <Select value={form.tipo} onValueChange={(v) => set('tipo', v as TipoRegra)}>
                <SelectTrigger id="regra-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tipo && <p className="text-xs text-muted-foreground">{tipo.dica}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="regra-recorrencia">Quando</Label>
              <Select
                value={recorrencia}
                onValueChange={(v) => {
                  // As exceções digitadas seguem a nova recorrência (dia ↔ período).
                  setLinhas((atuais) => adaptarLinhas(atuais, v as Recorrencia));
                  set('recorrencia', v as Recorrencia);
                }}
              >
                <SelectTrigger id="regra-recorrencia">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECORRENCIAS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {recorrencia === 'pascoa' ? (
            <div className="space-y-2">
              <Label htmlFor="regra-offset">Dias em relação à Páscoa</Label>
              <Input
                id="regra-offset"
                inputMode="numeric"
                className="sm:max-w-40"
                value={offset}
                onChange={(e) => setOffset(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Negativo é antes: −48 segunda e −47 terça de Carnaval, −2 Sexta-feira Santa, +60 Corpus Christi.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="regra-data">{recorrencia === 'anual' ? 'Dia' : 'De'}</Label>
                <Input id="regra-data" type="date" value={form.data} onChange={(e) => set('data', e.target.value)} />
                {recorrencia !== 'unica' && (
                  <p className="text-xs text-muted-foreground">Só o dia e o mês contam; o ano é ignorado.</p>
                )}
              </div>
              {(recorrencia === 'periodo_anual' || recorrencia === 'unica') && (
                <div className="space-y-2">
                  <Label htmlFor="regra-data-fim">{recorrencia === 'unica' ? 'Até (se for período)' : 'Até'}</Label>
                  <Input
                    id="regra-data-fim"
                    type="date"
                    value={form.data_fim ?? ''}
                    onChange={(e) => set('data_fim', e.target.value || null)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="regra-abrangencia">Onde vale</Label>
            <Select value={abrangencia} onValueChange={(v) => set('abrangencia', v as Abrangencia)}>
              <SelectTrigger id="regra-abrangencia">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ABRANGENCIAS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {abrangencia === 'estadual' && (
            <div className="space-y-2">
              <Label htmlFor="regra-uf">UF</Label>
              <Select value={form.uf ?? ''} onValueChange={(v) => set('uf', v)}>
                <SelectTrigger id="regra-uf" className="sm:max-w-40">
                  <SelectValue placeholder="Escolha" />
                </SelectTrigger>
                <SelectContent>
                  {UFS.map((uf) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {abrangencia === 'municipal' && (
            <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
              <div className="space-y-2">
                <Label htmlFor="regra-ibge">Código IBGE</Label>
                <Input
                  id="regra-ibge"
                  inputMode="numeric"
                  maxLength={7}
                  value={ibge}
                  onChange={(e) => setIbge(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="regra-municipio">Município</Label>
                <Input
                  id="regra-municipio"
                  maxLength={120}
                  value={form.municipio_nome ?? ''}
                  onChange={(e) => set('municipio_nome', e.target.value)}
                />
              </div>
            </div>
          )}

          {(abrangencia === 'tribunal' || abrangencia === 'tribunal_2grau' || abrangencia === 'unidade') && (
            <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
              <div className="space-y-2">
                <Label htmlFor="regra-tribunal">Tribunal</Label>
                <Input
                  id="regra-tribunal"
                  placeholder="TJAL"
                  maxLength={16}
                  value={form.tribunal ?? ''}
                  onChange={(e) => set('tribunal', e.target.value.toUpperCase())}
                />
              </div>
              {abrangencia === 'unidade' && (
                <div className="space-y-2">
                  <Label htmlFor="regra-orgao">Unidade</Label>
                  <Input
                    id="regra-orgao"
                    placeholder="Como vem no nome do órgão da intimação"
                    maxLength={200}
                    value={form.orgao ?? ''}
                    onChange={(e) => set('orgao', e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Justiças</legend>
            <p className="text-xs text-muted-foreground">
              Nenhuma marcada: vale em todas. Ex.: um feriado estadual que a Justiça Federal não observa.
            </p>
            <div className="flex flex-wrap gap-2">
              {JUSTICAS.map((j) => {
                const marcada = form.justicas?.includes(j.value) ?? false;
                return (
                  <Button
                    key={j.value}
                    type="button"
                    size="sm"
                    variant={marcada ? 'default' : 'outline'}
                    className="min-h-11"
                    aria-pressed={marcada}
                    onClick={() => alternarJustica(j.value)}
                  >
                    {j.label}
                  </Button>
                );
              })}
            </div>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="regra-ato">Ato que declara</Label>
            <Input
              id="regra-ato"
              placeholder="Lei, resolução ou portaria — com número e data"
              maxLength={300}
              value={form.ato_normativo}
              onChange={(e) => set('ato_normativo', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="regra-observacao">Observação</Label>
            <Textarea
              id="regra-observacao"
              rows={2}
              maxLength={300}
              value={form.observacao ?? ''}
              onChange={(e) => set('observacao', e.target.value)}
            />
          </div>

          {recorrencia !== 'unica' && <ExcecoesEditor recorrencia={recorrencia} linhas={linhas} onChange={setLinhas} />}

          {erro && (
            <Alert variant="destructive">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={salvar.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={salvar.isPending}>
              {salvar.isPending && <Spinner className="mr-2 text-inherit" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
